import Input from '../../../Input/Input';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { useParams } from 'react-router-dom';
import {
  useAddIntegrationMutation,
  useGetProjectByIdQuery,
  useLazyGetZendeskConsentLinkQuery,
  useUpdateIntegrationMutation,
} from 'src/services';
import { getSubdomainHelper } from 'src/helpers/getSubdomainHelper';
import { Button } from 'src/components/ui/button';
import ArrowLargeIcon from 'src/assets/img/arrow-large-icon.svg';
import { clsx } from 'clsx';
import { setSelectedIntegration } from 'src/store/slices/integrationSlice';
import { useAppSelector, useAppDispatch } from 'src/store';
import { useNotification } from 'src/hooks/useNotification';
import { useLoader } from 'src/provider/LoaderProvider';
import { Spinner } from 'src/components/Spinner/Spinner';
import type { SelectedIntegration } from 'src/store/slices/integrationSlice';
import type { IntegrationSubmitHandler } from 'src/components/Integrations/integrationFormTypes';
import { useIntegrationAuthorizationPolling } from 'src/components/Integrations/useIntegrationAuthorizationPolling';

interface ZendeskChatFormValues {
  subdomain: string;
  app_id: string;
  conversation_key_id: string;
  conversation_secret_key: string;
}

export const IntegrationFormZendeskChat = ({
  integration,
  onSubmit,
}: {
  integration?: SelectedIntegration;
  onSubmit?: IntegrationSubmitHandler;
}) => {
  const { id } = useParams();
  const { data: currentProject } = useGetProjectByIdQuery({ id: id });
  const { selectedIntegration } = useAppSelector((s) => s.integrationSlices);
  const createdIntegration = selectedIntegration?.id ? selectedIntegration : null;
  const notification = useNotification();
  const dispatch = useAppDispatch();
  const loader = useLoader();
  const isZendeskAuthorized =
    createdIntegration?.integration_data?.auth_type === 'oauth' &&
    createdIntegration?.integration_data?.auth_status === 'ok';
  const [addIntegration] = useAddIntegrationMutation();
  const [updateIntegration] = useUpdateIntegrationMutation();
  const [getZendeskConsentLinkQuery] = useLazyGetZendeskConsentLinkQuery();
  const { isCheckingAuthStatus, pollZendeskAuthorization } = useIntegrationAuthorizationPolling(id);

  const { handleSubmit, control } = useForm({
    defaultValues: {
      subdomain: (createdIntegration ?? integration)?.integration_data?.subdomain || '',
      app_id: (createdIntegration ?? integration)?.integration_data?.app_id || '',
      conversation_key_id:
        (createdIntegration ?? integration)?.integration_data?.conversation_key_id || '',
      conversation_secret_key: '',
    },
    resolver: yupResolver(
      object({
        subdomain: stringScheme({ required: true }).label('Zendesk subdomain'),
        app_id: stringScheme({ required: true }).label('App ID'),
        conversation_key_id: stringScheme({ required: true }).label('Key ID'),
        conversation_secret_key: stringScheme({ required: true }).label('Secret key'),
      }),
    ),
  });

  const buildBody = (data: ZendeskChatFormValues) => ({
    ...integration,
    integration_data: {
      ...integration.integration_data,
      ...createdIntegration?.integration_data,
      ...data,
      subdomain: getSubdomainHelper(data.subdomain)?.subdomain || data.subdomain,
      project_name: currentProject.name,
    },
  });

  const onConnectWithZendesk = async (data: ZendeskChatFormValues) => {
    let connectedIntegration = createdIntegration;

    loader.show();

    if (!connectedIntegration) {
      const body = buildBody(data);
      const { data: addIntegrationData, error } = await addIntegration({
        id,
        body: {
          integration_type: body.integration_type,
          integration_data: body.integration_data,
        },
      });

      if (!error) {
        connectedIntegration = { ...selectedIntegration, ...addIntegrationData };
        notification.success(
          `Integration ${selectedIntegration?.friendlyName || addIntegrationData.integration_type} was added.`,
        );
        dispatch(setSelectedIntegration(connectedIntegration));
      }
    } else {
      // Zendesk authorizes the integration as stored, so the current values (e.g. an edited
      // subdomain) are saved before requesting consent again.
      const body = buildBody(data);
      const { data: updatedIntegration, error } = await updateIntegration({
        projectId: id,
        integrationId: connectedIntegration.id,
        body: {
          integration_type: body.integration_type,
          integration_data: body.integration_data,
        },
      });

      if (!error) {
        connectedIntegration = { ...connectedIntegration, ...updatedIntegration };
        notification.success(
          `Integration ${selectedIntegration?.friendlyName || updatedIntegration.integration_type} was updated.`,
        );
        dispatch(setSelectedIntegration(connectedIntegration));
      }
    }

    loader.hide();

    if (connectedIntegration?.id) {
      const { data: consentLink } = await getZendeskConsentLinkQuery({
        project_id: id,
        integration_id: connectedIntegration.id,
        type: 'zendesk-chat',
      });

      if (consentLink?.authorize_url) {
        window.open(consentLink.authorize_url);
        pollZendeskAuthorization(
          connectedIntegration.id,
          (authorizedIntegration) =>
            dispatch(setSelectedIntegration({ ...connectedIntegration, ...authorizedIntegration })),
          // A chat integration counts as authorized only once connected through OAuth.
          ({ integration_data }) =>
            integration_data?.auth_type === 'oauth' && integration_data?.auth_status === 'ok',
        );
      }
    }
  };

  // Runs the same validation as submit; the connect handler only fires for a valid form.
  const connectWithZendesk = handleSubmit(onConnectWithZendesk);

  const submit = async (data: ZendeskChatFormValues) => {
    // Until the integration exists and Zendesk OAuth is authorized, Save runs the connect flow.
    const isNotCreated = !createdIntegration;
    const isNotAuthorized =
      createdIntegration?.integration_data?.auth_type === 'oauth' &&
      createdIntegration?.integration_data?.auth_status !== 'ok';

    if (isNotCreated || isNotAuthorized) {
      return onConnectWithZendesk(data);
    }

    const body = buildBody(data);

    if (createdIntegration) {
      const { data: updatedIntegration } = await updateIntegration({
        projectId: id,
        integrationId: createdIntegration.id,
        body: {
          integration_type: body.integration_type,
          integration_data: body.integration_data,
        },
      });

      dispatch(setSelectedIntegration({ ...createdIntegration, ...updatedIntegration }));
      return onSubmit(body, true);
    }

    onSubmit(body);
  };

  return (
    <form id="integration-form" onSubmit={handleSubmit(submit)}>
      {isCheckingAuthStatus && (
        <div
          className={
            'absolute flex items-center justify-center w-full h-full top-0 left-0 bg-ebony_opacity_30 backdrop-blur-sm z-50'
          }
        >
          <div
            className={
              'flex flex-col items-center gap-4 bg-light p-4 rounded-sm-5 text-sm font-semibold'
            }
          >
            <Spinner className={'!w-4 !border-[3px] !border-r-ebony_opacity_50'} />
            <p>Checking Zendesk Connection...</p>
          </div>
        </div>
      )}
      <div className="content flex flex-col gap-1.5">
        <Controller
          control={control}
          name="subdomain"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              type={'text'}
              label={'Zendesk subdomain'}
              placeholder={'domain.com'}
              invalidMessage={error?.message}
              readonly={isZendeskAuthorized}
              value={value}
              isValid={!error?.message}
              onChange={(e) => {
                const res = getSubdomainHelper(e.target.value);
                if (res?.subdomain) {
                  onChange(res?.subdomain);
                } else {
                  onChange(e.target.value);
                }
              }}
              infoTooltip={{ side: 'right', description: 'Link to your Zendesk workspace.' }}
            />
          )}
        />
        <Controller
          control={control}
          name="app_id"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              type={'text'}
              label={'App ID'}
              placeholder={'654321'}
              invalidMessage={error?.message}
              value={value}
              isValid={!error?.message}
              onChange={onChange}
              infoTooltip={{
                side: 'right',
                description: (
                  <>
                    Unique numeric identifier assigned to each
                    <br /> app installed in your Zendesk account.
                  </>
                ),
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="conversation_key_id"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              type={'text'}
              label={'Key ID'}
              placeholder={'abc123def456ghi789jkl0'}
              invalidMessage={error?.message}
              value={value}
              isValid={!error?.message}
              onChange={onChange}
              infoTooltip={{
                side: 'right',
                description: (
                  <>
                    Used in JWT authentication to identify
                    <br /> the key used to sign a token.
                  </>
                ),
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="conversation_secret_key"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              type={'text'}
              label={'Secret key'}
              placeholder={'x9Xb7gFQ2ZktYp3LqHNdMvuRJW5kq8Eo'}
              invalidMessage={error?.message}
              value={value}
              isValid={!error?.message}
              onChange={onChange}
              infoTooltip={{
                side: 'right',
                description: (
                  <>
                    Confidential string used with your OAuth client to
                    <br /> securely authenticate your app with Zendesk's API.
                  </>
                ),
              }}
            />
          )}
        />
      </div>
      <div className={'mt-5 flex flex-col gap-2'}>
        <Button
          type={'button'}
          size={'sm'}
          onClick={connectWithZendesk}
          disabled={isZendeskAuthorized}
          className={clsx(
            'w-full gap-2 px-8',
            isZendeskAuthorized
              ? 'bg-purple_150 text-purple_600 font-medium shadow-none disabled:opacity-100'
              : 'font-semibold bg-ebony text-white hover:bg-ebony_opacity_80 shadow-[0_1px_1px_0_rgba(5,5,13,0.25)]',
          )}
        >
          {isZendeskAuthorized ? (
            'Connected successfully with Zendesk'
          ) : (
            <>
              {'Connect with Zendesk'}
              <img src={ArrowLargeIcon} alt={''} width={16} height={16} />
            </>
          )}
        </Button>
        <p className={'text-center text-[13px] font-medium text-tertiary'}>
          You’ll be redirected to Zendesk to authorize access.
        </p>
      </div>
    </form>
  );
};
